import * as React from "react";
import {Slot} from "@radix-ui/react-slot";
import {cva, type VariantProps} from "class-variance-authority";

import {cn} from "../lib/utils";

const buttonVariants = cva("inline-flex items-center justify-center gap-sm rounded-md text-sm leading-none font-bold whitespace-nowrap cursor-pointer transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:translate-y-[0.5px] [&_svg:not([class*='size-'])]:size-4 motion-safe:active:scale-[0.97]", {
  variants: {
    variant: {
      default: "bg-primary text-primary-foreground shadow-xs hover:bg-primary/80 motion-safe:hover:-translate-y-[2px] hover:shadow-md border border-primary focus-visible:ring-ring",
      destructive: "bg-destructive text-white shadow-xs hover:bg-destructive/80 motion-safe:hover:-translate-y-[2px] hover:shadow-md focus-visible:ring-ring",
      outline: "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground motion-safe:hover:-translate-y-[2px] hover:shadow-md",
      secondary: "bg-secondary text-primary shadow-xs border hover:bg-primary hover:text-white motion-safe:hover:-translate-y-[2px] hover:shadow-md",
      ghost: "hover:bg-accent hover:text-accent-foreground",
      outlineGhost: "border border-dashed hover:bg-accent hover:text-accent-foreground",
      link: "text-primary underline-offset-4 hover:underline",
    },
    size: {
      default: "h-(--button-h-default) p-md text-md",
      xs: "h-lg px-[.75rem] text-sm leading-none tracking-wide [&_svg:not([class*='size-'])]:size-3.5",
      sm: "h-(--button-h-sm) px-sm text-sm",
      lg: "h-2xl rounded-md px-6 has-[>svg]:px-4",
      icon: "h-(--button-h-icon) w-(--button-h-icon) [&_svg]:size-[20px]",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return <Comp data-slot="button" className={cn(buttonVariants({variant, size, className}))} {...props} />;
}

export {Button, buttonVariants};
